import React, { useCallback, useState, useEffect, useRef } from 'react';
import { PopupProvider } from '../providers/PopupProvider';
import { ToastProvider } from '../providers/ToastProvider';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import Notification from './Notification';
import SessionTimeout from '../components/SessionTimeout';
import { useNavigate } from "react-router-dom";
import ViewData from "../components/ViewData";
import { TabPanel, TabView } from "primereact/tabview";
import { ColumnUtil } from '../utils/ColumnUtil';
import { IColumnDefinition } from '../types/Interfaces';
import { TQuery } from "../types/types";
import EnvironmentBanner from '../components/EnvBanner';
import { Environment } from "../../shared-api/helpers/Environment";
import { useAuth } from "../hooks/useAuth";
import Loading from '../components/Loading';

// Novas importações para a re-hidratação
import { TokenUtil } from '../utils/Utils';
import { UserController } from "../../mtlogin-api/controllers/MTLoginUserController";
import Remuneracao from '../components/Remuneracao';

const Home: React.FC = () => {

    // 1. Trazemos a função 'login' para conseguir repovoar a sessão
    const { session, login } = useAuth();
    const navigate = useNavigate();

    // 2. Trava para evitar que o React dispare a restauração duas vezes
    const reidratando = useRef(false);

    // 3. O motor da re-hidratação (F5)
    useEffect(() => {
        const restaurarSessao = async () => {
            if (!session?.loggedIn && !reidratando.current) {
                reidratando.current = true; // Aciona a trava

                const tokenSalvo = TokenUtil.getToken();

                if (tokenSalvo) {
                    try {
                        const userController = new UserController();
                        const sessionData = await userController.getUserInformation();

                        if (sessionData && sessionData.loggedIn) {
                            // Devolve os dados para a memória do React
                            login(sessionData.loggedIn, sessionData.registered);
                        } else {
                            navigate('/logininitiator');
                        }
                    } catch (error) {
                        console.error("Erro ao restaurar sessão:", error);
                        navigate('/logininitiator');
                    }
                } else {
                    navigate('/logininitiator');
                }
            }
        };

        restaurarSessao();
    }, [session, login, navigate]);

    // 4. O estado 'show' agora reage à volta da sessão
    const [show, setShow] = useState<boolean>(false);

    useEffect(() => {
        if (session?.loggedIn?.guid) {
            setShow(localStorage.getItem(session.loggedIn.guid) === 'true');
        }
    }, [session?.loggedIn?.guid]);

    const timeout = Number(Environment.get('REACT_APP_TIMEOUT_IN_MINUTES', '10'));

    const handleHide = useCallback((): void => {
        setShow(true);
    }, []);

    const handleLogout = useCallback(async () => {
        navigate('/signout');
    }, [navigate]);

    return (
        !session?.loggedIn ?
            <Loading /> :
            <>
                <SessionTimeout timeInMinutes={timeout} action={handleLogout} />
                <EnvironmentBanner />

                {show ?
                    <Content onLogout={handleLogout} /> :
                    <Notification onHide={handleHide} />}

                <Footer />
            </>
    );
}

interface IDataForm {
    label: string;
    key: TQuery;
    icon: string;
    component?: {
        title: string;
        label: string;
        columns: IColumnDefinition[];
    };
    customContent?: React.ReactNode;
}

const Content = ({ onLogout }: { onLogout: () => void }) => {

    const dataForms: IDataForm[] = [
        {
            label: 'Dívida Ativa Estadual',
            key: 'CDA',
            icon:'pi pi-search mr-2',
            component: {
                title: "Consultar Dívida Ativa",
                label: "Na seção a seguir, podem ser consultados todos os débitos registrados como dívida ativa estadual.",
                columns: ColumnUtil.createColumnsDefinition({
                    numeroKey: "numeroCDA",
                    numeroHeader: "Número CDA",
                    dataInscricaoKey: "dataInscricaoCDA",
                    dataInscricaoHeader: "Data Inscrição CDA",
                })
            }
        },
        {
            label: 'Requisição de Pequeno Valor',
            key: 'RPV',
            icon:'pi pi-search mr-2',
            component: {
                title: "Consultar Requisição de Pequeno Valor",
                label: "Na seção abaixo, podem ser consultados todos os pagamentos registrados como requisições de pequeno valor.",
                columns: ColumnUtil.createColumnsDefinition({
                    numeroKey: "numeroRPV",
                    numeroHeader: "Processo RPV",
                    dataInscricaoKey: "dataInscricaoRPV",
                    dataInscricaoHeader: "Data Inscrição RPV",
                })
            }
        },
        {
            label: 'Remuneração',
            key: 'RMNRC',
            icon:'pi pi-file mr-2',
            customContent: <Remuneracao />
        }
    ];

    return (
        <PopupProvider>
            <ToastProvider>
                <Navbar onLogout={onLogout} />
                <TabView>
                    {dataForms.map(dataForm =>
                        <TabPanel header={dataForm.label} key={dataForm.key} leftIcon={dataForm.icon}>
                            {dataForm.customContent ? (
                                dataForm.customContent
                            ) : (
                                dataForm.component && (
                                    <ViewData
                                        title={dataForm.component.title}
                                        category={dataForm.key as TQuery}
                                        label={dataForm.component.label}
                                        columns={dataForm.component.columns}
                                    />
                                )
                            )}
                        </TabPanel>
                    )}
                </TabView>
            </ToastProvider>
        </PopupProvider>
    );
}

export default Home;